import axios from "axios"

// Cliente HTTP del timbrador para los endpoints /a7/*.
// El back exige la X-API-Key en cada request — está hardcodeada porque
// es la misma que se usa en Comprobantes.js para ZIP y reenvío.
// TODO: mover a variable de entorno cuando se haga el proxy server-side.
const API_KEY = "394beb1f7fcf7793dc79ba0a5fe2077cca2bf99c8407dfec5325a7335505f2a5"
const BASE = "https://timbrador.ls-da.com/a7"

const client = axios.create({
  baseURL: BASE,
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": API_KEY,
  },
  timeout: 60000,
})

// ───────── Helpers de parámetros ─────────

const cleanParams = obj =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined),
  )

// ───────── Emisores / Dashboard ─────────

/** Lista de emisores con contadores agregados — selector multi-tenant. */
export const getEmisores = ({ desde, hasta } = {}) =>
  client.get("/emisores", { params: cleanParams({ desde, hasta }) })

/**
 * Dashboard completo de un emisor: tarjetas, engagement, distribuciones,
 * tendencias, performance, top sucursales/destinatarios, errores, etc.
 * Una sola llamada que trae todo el resumen.
 */
export const getResumenEmisor = (nitemisor, { desde, hasta } = {}) =>
  client.get(`/emisores/${encodeURIComponent(nitemisor)}/resumen`, {
    params: cleanParams({ desde, hasta }),
  })

// ───────── Inbox (envíos) ─────────

/** Listado paginado de envíos con filtros (estado, fechas, prefijo/folio, cufe). */
export const getInbox = (filtros = {}) =>
  client.get("/inbox", { params: cleanParams(filtros) })

/** Detalle resumido de un CUFE. */
export const getInboxCufe = cufe =>
  client.get(`/inbox/${encodeURIComponent(cufe)}`)

/** Detalle completo de un CUFE con timeline y eventos SES. */
export const getInboxFull = cufe =>
  client.get(`/inbox/${encodeURIComponent(cufe)}/full`)

/** Historial completo de cambios de estado del CUFE. */
export const getInboxHistorial = cufe =>
  client.get(`/inbox/${encodeURIComponent(cufe)}/historial`)

// ───────── Errores (agregado por emisor) ─────────

/**
 * Resumen ejecutivo de errores: cards, donuts (por etapa/bounce/origen/tipoPdf),
 * tendencia diaria, tablas TOP (receptores, destinatarios, sucursales, mensajes).
 * El back acepta filtros opcionales `error_stage` y `nit_receptor` para que
 * los KPIs/donuts se filtren consistentemente con la lista de la tabla.
 */
export const getErroresSummary = ({ nitemisor, desde, hasta, error_stage, nit_receptor } = {}) =>
  client.get("/errores/summary", {
    params: cleanParams({ nitemisor, desde, hasta, error_stage, nit_receptor }),
  })

/**
 * Listado paginado de errores con filtros opcionales:
 * error_stage, bounce_type, nit_receptor, recipient, prefijo, cufe,
 * source (listener/batch/retry/manual), desde, hasta, limit, offset.
 */
export const getErroresList = (filtros = {}) =>
  client.get("/errores", { params: cleanParams(filtros) })

// ───────── Bounces ─────────

/** Resumen de bounces para cards/gráficos (totales, por tipo, etc.). */
export const getBouncesSummary = ({ nitemisor, desde, hasta } = {}) =>
  client.get("/bounces/summary", { params: cleanParams({ nitemisor, desde, hasta }) })

/** Listado detallado de bounces con filtros (origen, bounce_type, recipient). */
export const getBounces = (filtros = {}) =>
  client.get("/bounces", { params: cleanParams(filtros) })

// ───────── Acciones ─────────

/** Reenvía un correo. `mailReceptor` opcional para cambiar el destinatario. */
export const reenviarCorreo = ({ cufe, mailReceptor }) =>
  client.post("/reenviar", cleanParams({ cufe, mailReceptor }))

/**
 * Genera un ZIP firmado del CUFE. Requiere `passwordCertificado` y
 * opcionalmente `tipopdf`. Usa `?format=json` para recibir base64.
 */
export const generarZip = ({ cufe, passwordCertificado, tipopdf, format }) =>
  client.post(
    "/zip/generate",
    cleanParams({ cufe, passwordCertificado, tipopdf }),
    { params: cleanParams({ format }), responseType: format === "json" ? "json" : "blob" },
  )

export default client
